/**
 * Centralized error handling for question access restrictions.
 *
 * AIDEV-NOTE: This module handles 403 responses from the question API
 * and parses structured error details for appropriate UI rendering.
 *
 * The server names an Event, a Space or an author only when the viewer may know of it, and sends
 * `null` otherwise (backend docs/features/post_read_access.md 2.4), so every object here may be
 * absent. This parser is the one seam the refusal placeholders read through: an object reaches
 * them with a usable `id` and a `name` that is a non-blank string or null, or not at all.
 */

/**
 * Error codes returned by the backend for 403 responses
 */
export enum QuestionAccessErrorCode {
  /** Guest user trying to access private question - needs to sign in */
  LOGIN_REQUIRED = 'login_required',
  /** Authenticated user not going to an Event that would open the post - names that Event, or null */
  EVENT_CHECKIN_REQUIRED = 'event_checkin_required',
  /** Authenticated user not in author's network - can't easily fix */
  NETWORK_ONLY = 'network_only',
  /** Authenticated user not a member of the post's Space - names that Space, or null */
  SPACE_MEMBERSHIP_REQUIRED = 'space_membership_required',
}

/**
 * Event info returned when going to the Event would open the post
 */
export interface EventAccessInfo {
  id: string;
  /** Null when the server sent no name, or a blank one: say "the Event", never "". */
  name: string | null;
  slug?: string | null;
  image_url?: string | null;
}

/**
 * Author info returned for network-only questions (if author has public profile)
 */
export interface AuthorAccessInfo {
  id: string;
  /** Null when the server sent no name, or a blank one (`users.full_name` may be blank). */
  name: string | null;
  slug?: string | null;
  profile_picture?: string | null;
}

/**
 * Space info returned when Space membership is required
 */
export interface SpaceAccessInfo {
  id: string;
  /** Null when the server sent no name, or a blank one: say "the Space", never "". */
  name: string | null;
  slug?: string | null;
  image_url?: string | null;
}

/**
 * Parsed access error with typed discriminated union
 */
export type QuestionAccessError =
  | { type: QuestionAccessErrorCode.LOGIN_REQUIRED }
  | { type: QuestionAccessErrorCode.EVENT_CHECKIN_REQUIRED; event: EventAccessInfo | null }
  | { type: QuestionAccessErrorCode.NETWORK_ONLY; author: AuthorAccessInfo | null }
  | { type: QuestionAccessErrorCode.SPACE_MEMBERSHIP_REQUIRED; space: SpaceAccessInfo | null };

/**
 * Extract HTTP status from various error shapes
 */
function getErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null;

  // Direct status property
  const directStatus = (error as { status?: number }).status;
  if (typeof directStatus === 'number') return directStatus;

  // Nested in response object (Axios pattern)
  const responseStatus = (error as { response?: { status?: number } }).response?.status;
  if (typeof responseStatus === 'number') return responseStatus;

  return null;
}

/**
 * Extract structured error detail from 403 response
 */
function getErrorDetail(error: unknown): unknown {
  if (!error || typeof error !== 'object') return null;

  // Axios error pattern: error.response.data.detail
  const axiosDetail = (error as { response?: { data?: { detail?: unknown } } }).response?.data
    ?.detail;
  if (axiosDetail !== undefined) return axiosDetail;

  // Direct detail property
  const directDetail = (error as { detail?: unknown }).detail;
  if (directDetail !== undefined) return directDetail;

  return null;
}

/** A string field as the placeholders read it: trimmed, or null when absent, blank or not text. */
function textOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The object's fields, when it is one a placeholder can use: an object with an `id` to navigate
 * or report by. Anything else is null. The server sends each object whole or null; this holds
 * the placeholders to that whatever arrives.
 */
function fieldsWithId(value: unknown): (Record<string, unknown> & { id: string }) | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = value as Record<string, unknown>;
  const id = textOrNull(fields.id);
  return id === null ? null : { ...fields, id };
}

/** An Event or a Space as a refusal names it; the two share the shape `{id, name, slug, image_url}`. */
function readPlace(
  value: unknown,
): { id: string; name: string | null; slug: string | null; image_url: string | null } | null {
  const fields = fieldsWithId(value);
  if (fields === null) return null;
  return {
    id: fields.id,
    name: textOrNull(fields.name),
    slug: textOrNull(fields.slug),
    image_url: textOrNull(fields.image_url),
  };
}

function readAuthor(value: unknown): AuthorAccessInfo | null {
  const fields = fieldsWithId(value);
  if (fields === null) return null;
  return {
    id: fields.id,
    name: textOrNull(fields.name),
    slug: textOrNull(fields.slug),
    profile_picture: textOrNull(fields.profile_picture),
  };
}

/**
 * Parse a question fetch error and return structured access error info
 *
 * @param error - Error from useQuestionBySlugOrId or similar query
 * @returns Parsed QuestionAccessError or null if not a 403
 *
 * @example
 * const accessError = parseQuestionAccessError(questionError);
 * if (accessError?.type === QuestionAccessErrorCode.EVENT_CHECKIN_REQUIRED) {
 *   // Show EventCheckinRequiredPlaceholder with accessError.event
 * }
 */
export function parseQuestionAccessError(error: unknown): QuestionAccessError | null {
  const status = getErrorStatus(error);

  // Only handle 403 errors
  if (status !== 403) return null;

  const detail = getErrorDetail(error);

  // Handle structured error response (authenticated user scenarios)
  if (typeof detail === 'object' && detail !== null) {
    const structuredDetail = detail as {
      error_code?: unknown;
      event?: unknown;
      author?: unknown;
      space?: unknown;
    };

    if (structuredDetail.error_code === QuestionAccessErrorCode.EVENT_CHECKIN_REQUIRED) {
      return {
        type: QuestionAccessErrorCode.EVENT_CHECKIN_REQUIRED,
        event: readPlace(structuredDetail.event),
      };
    }

    if (structuredDetail.error_code === QuestionAccessErrorCode.NETWORK_ONLY) {
      return {
        type: QuestionAccessErrorCode.NETWORK_ONLY,
        author: readAuthor(structuredDetail.author),
      };
    }

    if (structuredDetail.error_code === QuestionAccessErrorCode.SPACE_MEMBERSHIP_REQUIRED) {
      return {
        type: QuestionAccessErrorCode.SPACE_MEMBERSHIP_REQUIRED,
        space: readPlace(structuredDetail.space),
      };
    }
  }

  // Fallback: guest user, legacy response, or unrecognized error_code
  // All treated as login_required since we can't provide specific guidance
  return { type: QuestionAccessErrorCode.LOGIN_REQUIRED };
}

/**
 * Check if an error is a 403 access denied error
 */
export function isAccessDeniedError(error: unknown): boolean {
  return getErrorStatus(error) === 403;
}
